import { unwrap, type TemplateOut, type WorkspaceOut } from "@sagot/api-client";
import { canonicalIndustry } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { localized, primaryTemplate } from "./ranking";
import { readOnboarding } from "./use-onboarding";

/** `GET /v1/industry-templates` — template data incl. industries, goals and follow-up fields. */
export function useTemplates() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.templates, queryFn: () => unwrap(api.GET("/v1/industry-templates")), staleTime: Infinity });
}

/** `GET /v1/setup-goals` — the goal catalogue (labels + what each goal pre-enables). */
export function useSetupGoals() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.setupGoals, queryFn: () => unwrap(api.GET("/v1/setup-goals")), staleTime: Infinity });
}

/**
 * The setup's template: the one picked during onboarding (step 1 or the step-3 switcher), else the
 * workspace's starting `template_key`, else the industry's primary one.
 */
export function workspaceTemplate(templates: readonly TemplateOut[] | undefined, workspace: WorkspaceOut | undefined): TemplateOut | undefined {
  if (!templates || !workspace) return undefined;
  const wanted = readOnboarding(workspace).data.template_key ?? workspace.template_key;
  return templates.find((tpl) => tpl.key === wanted) ?? primaryTemplate(templates, canonicalIndustry(workspace.industry));
}

export interface SetupCopy {
  assistantName: string;
  knowledgeExamples: string;
  sensitive: string;
  starterQuestions: readonly string[];
}

/** Later-step examples from template data (never hard-coded industry copy). */
export function useSetupCopy(workspace: WorkspaceOut | undefined): SetupCopy {
  const { t, i18n } = useTranslation();
  const templates = useTemplates();
  const template = workspaceTemplate(templates.data, workspace);
  const copy = template?.onboarding_copy;
  return {
    assistantName: localized(copy?.assistant_name, i18n.language) || t("onboarding.configure.defaultName"),
    knowledgeExamples: localized(copy?.knowledge_examples, i18n.language),
    sensitive: localized(copy?.sensitive_examples, i18n.language) || t("onboarding.knowledge.sensitiveDefault"),
    starterQuestions: template?.starter_questions ?? [],
  };
}
